<script setup>
import VueScrollTo from 'vue-scrollto';
import queryString from 'query-string';
import { computed, onMounted, ref } from 'vue';
// Store.
import { storeToRefs } from 'pinia';
import useProductFinderStore from '../../stores/productFinder';
// Components.
import CheckboxToggle from '../elements/CheckboxToggle';
import DialogModal from '../elements/DialogModal';
import MultiSelect from '../elements/MultiSelect';
import OrganicFarmingSwitch from './OrganicFarmingSwitch';
import SustainableApplicationExplanation from './SustainableApplicationExplanation';
// Utils.
import { expandCollapse } from '../../utils/expandCollapse';
import { isMobile } from '../../utils/isMobile';
import scrollLock from '../../utils/scrollLock';
import { default as t } from '../../translations/translationStrings';
import {
  ORGANIC_FARMING_ID,
  VUESCROLLTO_OPTIONS,
  SUSTAINABILITY_PAGE_ID,
} from '../../utils/constants';

// Data.
const onMobile = ref(isMobile());
const mobileFiltersOpen = ref(false);
const showSustainableApplicationModel = ref(false);
const showSustainableProductionModel = ref(false);
const preselectedModalTab = ref('');
const dialogSustainableApplication = ref(null);
const dialogSustainableProduction = ref(null);
const {
  crops,
  applications,
  targetAreas,
  selectedCrops,
  selectedApplications,
  selectedTargetAreas,
  sustainableApplicationFilters,
  sustainableProductionFilters,
  showOnlyOrganic,
  transitionName,
} = storeToRefs(useProductFinderStore());
// 3 template refs in total.
const aside = ref(null);
const mobileFiltersTrigger = ref(null);
const filtersBody = ref(null);
// In COMPWR-2028 in was requested to show/hide sustainable filters based on the products.
// The check should be performed only once (on mounted), so no need for computed props.
// (i think this doesn't work like it is, for users - surely if you change the filters after the page is loaded
// sustainability filters can become relevant again?, but maybe it does so @todo check)
const showSustainableApplicationFilters = ref(true);
const showSustainableProductionFilters = ref(true);
// Non-reactive data.
const sidebarScrollOffset = 20;
// Props.
const props = defineProps({
  lang: {
    type: String,
    required: true,
    default: '',
  },
});

// Computed & store getters.
const {
  getMainCrops,
  getCropsByParent,
  getActiveFiltersCount,
  getFilteredProducts,
} = useProductFinderStore();
const mainCrops = computed(() => getMainCrops());
const activeFiltersCount = computed(() => getActiveFiltersCount());
const filteredProducts = computed(() => getFilteredProducts());

// Methods & store actions.
const toggleMobileFilters = () => {
  // Expand only if we are on mobile.
  if (onMobile.value) {
    if (mobileFiltersOpen.value) {
      expandCollapse(filtersBody.value, mobileFiltersTrigger.value);
      scrollLock.unlock();
      mobileFiltersOpen.value = false;
    } else {
      // Scroll to 20px above filters head when opening (s. COMPWR-2031).
      VueScrollTo.scrollTo(mobileFiltersTrigger.value, {
        ...VUESCROLLTO_OPTIONS,
        force: true,
        offset: -20,
        onDone: () => {
          scrollLock.lock();
          expandCollapse(filtersBody.value, mobileFiltersTrigger.value);
          mobileFiltersOpen.value = true;
        },
      });
    }
  }
};

const updateUrl = () => {
  const toParse = {};
  toParse.crops = [...new Set(selectedCrops.value)];
  toParse.applications = [...new Set(selectedApplications.value)];
  toParse.targets = [...new Set(selectedTargetAreas.value)];
  toParse.sustainableApplication = Object.keys(
    sustainableApplicationFilters.value,
  ).filter((key) => sustainableApplicationFilters.value[key]);
  toParse.sustainableProduction = Object.keys(
    sustainableProductionFilters.value,
  ).filter((key) => sustainableProductionFilters.value[key]);

  const query = queryString.stringify(toParse, { arrayFormat: 'comma' });
  window.history.pushState({}, document.title, `?${query}`);

  // Whenever the URL gets updated, products are shuffled.
  transitionName.value = 'products-shuffle';

  // Clear up the URL if there's nothing selected.
  const allEmpty = Object.keys(toParse).every(
    (key) => toParse[key].length === 0,
  );
  if (allEmpty) {
    window.history.pushState(
      {},
      document.title,
      window.location.pathname + window.location.search,
    );
  }
};

const getItemNamesByIds = (items, itemIds) => {
  return items
    .filter((item) => itemIds.includes(item.id))
    .map((item) => item.name);
};

// Sync the organic farming switch with the target areas.
// No watcher as it would trigger itself each time.
const syncOrganicSwitch = (trigger = selectedTargetAreas.value) => {
  if (typeof trigger === 'boolean') {
    selectedTargetAreas.value = trigger
      ? [...selectedTargetAreas.value, ORGANIC_FARMING_ID]
      : selectedTargetAreas.value.filter((id) => id !== ORGANIC_FARMING_ID);
  } else {
    showOnlyOrganic.value =
      selectedTargetAreas.value.includes(ORGANIC_FARMING_ID);
  }

  // Update URL params.
  updateUrl();
};

const { resetFilters } = useProductFinderStore();
const reset = () => {
  resetFilters();

  // Update URL params.
  updateUrl();
};

// Add/remove classes controlling gradients on top of the scrollable filters.
// Re-using the same pattern found elsewhere (Related products), but it should be improved.
const handleSidebarScroll = (e) => {
  if (
    e.target.offsetHeight + e.target.scrollTop >=
    e.target.scrollHeight - sidebarScrollOffset
  ) {
    aside.value.classList.add('scroll--end');
    aside.value.classList.remove('scroll--start');
  } else if (e.target.scrollTop < sidebarScrollOffset) {
    aside.value.classList.add('scroll--start');
    aside.value.classList.remove('scroll--end');
  } else {
    aside.value.classList.add('scroll--end', 'scroll--start');
  }
};

// Sustainable application.
const preselectAndOpenModal = (key) => {
  preselectedModalTab.value = key;
  showSustainableApplicationModel.value = true;
  dialogSustainableApplication.value.openDialogModal();
};

// Sustainable production.
const openDialogModal = () => {
  showSustainableProductionModel.value = true;
  dialogSustainableProduction.value.openDialogModal();
};

// Mounted.
onMounted(() => {
  // Determine visibility of sustainability filters.
  showSustainableApplicationFilters.value = filteredProducts.value.some(
    (product) =>
      Object.keys(product.sustainableApplication).some(
        (key) => product.sustainableApplication[key],
      ),
  );
  showSustainableProductionFilters.value = filteredProducts.value.some(
    (product) =>
      Object.keys(product.sustainableProduction).some(
        (key) => product.sustainableProduction[key],
      ),
  );

  // Attach event listeners.
  window.addEventListener('resize', () => {
    onMobile.value = isMobile();
  });
});
</script>

<template>
  <aside ref="aside" class="scroll sticky">
    <div class="product-finder__filters" @scroll="handleSidebarScroll($event)">
      <div
        class="product-finder__filters-head"
        ref="mobileFiltersTrigger"
        @click="toggleMobileFilters">
        <p class="product-filter__filters-label">
          <span>{{ t.filterLabel }}</span>
          <!-- Number of filters active. -->
          <span v-if="activeFiltersCount" class="product-finder__filters-count">
            {{ activeFiltersCount }}
          </span>
        </p>

        <!-- Count of the visible products. -->
        <span class="product-finder__count">
          <Transition name="product-finder-slide-fade" mode="out-in">
            <span :key="filteredProducts.length">
              {{ filteredProducts.length }}
            </span>
          </Transition>
          {{ filteredProducts.length === 1 ? t.product : t.products }}
        </span>

        <!-- Reset filters link. -->
        <button
          v-if="activeFiltersCount > 0 && !onMobile"
          class="product-finder__reset"
          @click="reset">
          <i class="far fa-close"></i>
          {{ t.resetFiltersLabel }}
        </button>
      </div>

      <!-- Main filters. -->
      <div class="product-finder__filters-body" ref="filtersBody">
        <!-- Mobile reset filters link. -->
        <button
          v-if="activeFiltersCount > 0 && onMobile"
          class="product-finder__reset"
          @click="reset">
          <i class="far fa-close"></i>
          {{ t.resetFiltersLabel }}
        </button>

        <!-- Crops. -->
        <div class="product-finder__filter product-finder__filter--crops">
          <h3 class="product-finder__filter-label">
            {{ t.crops }}
          </h3>
          <div class="product-finder__filter-fields">
            <template v-for="item in mainCrops" :key="item.id">
              <MultiSelect
                :items="getCropsByParent(item.id)"
                :selectedItemNames="getItemNamesByIds(crops, selectedCrops)"
                :label="item.name"
                :labelEn="item.name_en"
                :allLabel="item.all_label"
                :icon="item.icon"
                :buttonSelectText="t.buttonSelectText"
                :parentId="item.id"
                v-model="selectedCrops"
                @update:modelValue="updateUrl" />
            </template>
          </div>
        </div>

        <!-- Product type (applications & target areas). -->
        <div
          class="product-finder__filter product-finder__filter--product-type">
          <h3 class="product-finder__filter-label">
            {{ t.productType }}
          </h3>
          <div class="product-finder__filter-fields">
            <MultiSelect
              :items="applications"
              :selectedItemNames="
                getItemNamesByIds(applications, selectedApplications)
              "
              :label="t.applicationLabel"
              :buttonSelectText="t.buttonSelectText"
              v-model="selectedApplications"
              @update:modelValue="updateUrl" />
            <MultiSelect
              :items="targetAreas"
              :selectedItemNames="
                getItemNamesByIds(targetAreas, selectedTargetAreas)
              "
              :label="t.targetAreasLabel"
              :buttonSelectText="t.buttonSelectText"
              v-model="selectedTargetAreas"
              @update:modelValue="syncOrganicSwitch" />
          </div>
        </div>

        <!-- Organic farming switch. -->
        <div
          class="product-finder__filter product-finder__filter--organic-farming">
          <OrganicFarmingSwitch
            v-model="showOnlyOrganic"
            @update:modelValue="syncOrganicSwitch(showOnlyOrganic)" />
        </div>

        <!-- Sustainable application. -->
        <div
          v-if="showSustainableApplicationFilters"
          class="product-finder__filter product-finder__filter--sustainable-application">
          <h3 class="product-finder__filter-label">
            {{ t.sustainableApplication.label }}
          </h3>

          <div
            class="product-finder__filter-fields product-finder__filter-fields--2cols">
            <template
              v-for="(item, key) in sustainableApplicationFilters"
              :key="key">
              <CheckboxToggle
                v-model="sustainableApplicationFilters[key]"
                :label="t.sustainableApplication[key]"
                :icon="`/${require(`../../../images/s12y-icons/icon-${key}-bg.svg`)}`"
                rounded-icon
                @update:modelValue="updateUrl" />
              <span
                class="product-finder__filter-modal-trigger"
                :title="t.learnMore"
                @click="preselectAndOpenModal(key)">
                <i class="far fa-info-circle"></i>
              </span>
            </template>
          </div>
        </div>

        <!-- Sustainable production. -->
        <div
          v-if="showSustainableProductionFilters"
          class="product-finder__filter product-finder__filter--sustainable-production">
          <h3 class="product-finder__filter-label">
            {{ t.sustainableProduction.label }}
          </h3>

          <div
            class="product-finder__filter-fields product-finder__filter-fields--2cols">
            <CheckboxToggle
              v-for="(item, key) in sustainableProductionFilters"
              :key="key"
              v-model="sustainableProductionFilters[key]"
              :label="t.sustainableProduction[key]"
              :icon="`/${require(`../../../images/s12y-icons/icon-${key}-bg.svg`)}`"
              rounded-icon
              @update:modelValue="updateUrl" />
            <span
              class="product-finder__filter-modal-trigger"
              :title="t.learnMore"
              @click="openDialogModal">
              <i class="far fa-info-circle"></i>
            </span>
          </div>
        </div>
      </div>
      <!-- A Fake 'Show results' button for mobile. -->
      <Transition name="slide-up-fade">
        <div
          v-if="onMobile && mobileFiltersOpen"
          @click="toggleMobileFilters"
          class="product-finder__filters-button">
          <button class="button button--product-finder">
            {{ t.showResults }}
          </button>
        </div>
      </Transition>
    </div>

    <!-- Modal holding sustainable application information. -->
    <DialogModal
      v-model="showSustainableApplicationModel"
      modal-type="sustainable-application"
      ref="dialogSustainableApplication">
      <!-- Header. -->
      <template #header>
        <span>{{ t.sustainableApplication.label }}</span>
      </template>
      <!-- Content. -->
      <template #content>
        <SustainableApplicationExplanation
          type="modal"
          :preselected-tab="preselectedModalTab" />
      </template>
    </DialogModal>

    <!-- Modal holding sustainable production information. -->
    <DialogModal
      v-model="showSustainableProductionModel"
      modal-type="co2-reduction"
      ref="dialogSustainableProduction">
      <!-- Header. -->
      <template #header>
        <span v-html="t.sustainableProduction.modal.title"></span>
      </template>
      <!-- Content. -->
      <template #content>
        <div class="text-formatted">
          <p
            v-for="(item, key) in t.sustainableProduction.modal.content"
            :key="key"
            v-html="item"></p>
          <a
            :href="`/${
              !props.lang ? '' : `${props.lang}/`
            }node/${SUSTAINABILITY_PAGE_ID}`">
            {{ t.sustainableProduction.modal.learnMore }}
          </a>
        </div>
      </template>
    </DialogModal>
  </aside>
</template>
